<template>
  <Card
    class="flex h-full min-w-0 w-full flex-col overflow-hidden rounded-[1.35rem] border border-[rgba(218,226,238,0.95)] bg-gradient-to-b from-white to-[#fbfcff] shadow-[0_20px_40px_rgba(15,23,42,0.05)]"
  >
    <CardHeader class="border-b border-[rgba(227,233,243,0.95)] px-6 py-5 pb-4">
      <div
        class="flex items-start justify-between gap-4 max-[900px]:flex-col max-[900px]:items-stretch"
      >
        <div class="space-y-2">
          <div>
            <CardTitle class="m-0 text-[1.2rem] font-extrabold text-[#182033]">
              {{ role === 'client' ? 'Package Performance Overview' : 'Service / Package KPI Overview' }}
            </CardTitle>
            <CardDescription class="mt-[0.35rem] text-[#6e7b92]">
              {{ role === 'client' ? 'See how your included services are progressing across the current period.' : chartDescription }}
            </CardDescription>
          </div>

          <div class="flex flex-wrap items-center gap-2">
            <Badge
              variant="outline"
              class="rounded-full border-[rgba(217,226,238,0.95)] bg-[#edf4ff] px-3 py-1 text-[0.76rem] font-extrabold text-[#2855a6]"
            >
              Showing: {{ scopeSummaryLabel }}
            </Badge>
            <Button
              v-if="showResetButton"
              variant="outline"
              type="button"
              class="h-8 rounded-full border-[rgba(49,125,255,0.22)] bg-[#f6f9ff] px-3 text-[0.76rem] font-bold text-[#2855a6] hover:bg-[#eef4ff]"
              :disabled="scopeLoading"
              @click="resetToAllScope"
            >
              Reset to All
            </Button>
          </div>
        </div>

        <div
          v-if="showScopeControls"
          class="min-w-[17rem] max-w-[23rem] flex-1 self-stretch rounded-[1rem] border border-[rgba(227,233,243,0.95)] bg-[#fbfcff] p-3"
        >
          <div class="flex flex-col gap-2">
            <span
              class="text-[0.72rem] font-bold uppercase tracking-[0.12em] text-[#718096]"
            >
              Account scope
            </span>
            <Select
              :model-value="selectedScope"
              :disabled="scopeLoading"
              @update:model-value="handleScopeSelect"
            >
              <SelectTrigger
                class="min-h-11 rounded-[0.9rem] border-[rgba(211,220,233,1)] bg-white px-[0.9rem] py-[0.74rem] text-[0.92rem] text-[#243049] shadow-[inset_0_1px_0_rgba(255,255,255,0.85)] focus:ring-[#8eb4ff] focus:ring-offset-0"
              >
                <SelectValue placeholder="All Client Accounts" />
              </SelectTrigger>
              <SelectContent
                class="rounded-[0.95rem] border-[rgba(211,220,233,1)] bg-white text-[#243049] shadow-[0_20px_45px_rgba(15,23,42,0.12)]"
              >
                <SelectItem value="all">
                  All Client Accounts
                </SelectItem>
                <SelectItem value="specific">
                  Specific Client Account
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </CardHeader>

    <CardContent class="flex-1 p-0">
      <div
        v-if="loadingState"
        class="space-y-5 p-6"
      >
        <div class="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <Skeleton
            v-for="n in 4"
            :key="`summary-${n}`"
            class="h-[6.5rem] rounded-[1.15rem] bg-stone-100"
          />
        </div>
        <div class="grid gap-4 xl:grid-cols-[1.05fr_1.55fr]">
          <Skeleton class="h-[20rem] rounded-[1.15rem] bg-stone-100" />
          <Skeleton class="h-[20rem] rounded-[1.15rem] bg-stone-100" />
        </div>
        <Skeleton class="h-[17rem] rounded-[1.15rem] bg-stone-100" />
      </div>

      <div
        v-else-if="currentError && !hasData"
        class="px-6 py-6"
      >
        <div
          class="grid min-h-[18rem] place-items-center rounded-[1.15rem] border border-dashed border-[#f4b4b4] bg-[#fff8f8] px-6 text-center"
        >
          <div class="max-w-md">
            <p class="text-[1rem] font-bold text-[#b42318]">
              Unable to load KPI overview.
            </p>
            <p class="mt-2 text-[0.92rem] leading-7 text-[#b42318]/80">
              {{ currentError }}
            </p>
          </div>
        </div>
      </div>

      <div
        v-else-if="!hasData"
        class="px-6 py-6"
      >
        <div
          class="grid min-h-[18rem] place-items-center rounded-[1.15rem] border border-dashed border-[rgba(218,226,238,0.95)] bg-[#fbfcff] px-6 text-center"
        >
          <div class="max-w-md">
            <p class="text-[1rem] font-bold text-[#182033]">
              {{ emptyTitle }}
            </p>
            <p class="mt-2 text-[0.92rem] leading-7 text-[#6e7b92]">
              {{ emptyDescription }}
            </p>
          </div>
        </div>
      </div>

      <div
        v-else
        class="space-y-5 px-6 py-6"
      >
        <div class="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <div
            v-for="item in summaryCards"
            :key="item.label"
            class="rounded-[1rem] border border-[rgba(227,233,243,0.95)] bg-white px-4 py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]"
          >
            <div class="flex items-start justify-between gap-3">
              <div>
                <p
                  class="text-[0.72rem] font-bold uppercase tracking-[0.12em] text-[#718096]"
                >
                  {{ item.label }}
                </p>
                <p
                  class="mt-2 text-[1.5rem] font-black leading-none text-[#182033]"
                >
                  {{ item.value }}
                </p>
              </div>
              <span
                class="mt-1 h-2.5 w-2.5 rounded-full"
                :style="{ backgroundColor: item.dot }"
              />
            </div>
            <p class="mt-3 text-[0.8rem] text-[#6e7b92]">
              {{ item.muted }}
            </p>
          </div>
        </div>

        <div class="grid gap-4 xl:grid-cols-[1.05fr_1.55fr]">
          <section
            class="rounded-[1.15rem] border border-[rgba(227,233,243,0.95)] bg-white p-4"
          >
            <div class="mb-3 flex items-center justify-between gap-3">
              <div>
                <p
                  class="text-[0.72rem] font-bold uppercase tracking-[0.14em] text-[#718096]"
                >
                  {{ primaryChartLabel }}
                </p>
                <p class="mt-1 text-[0.88rem] text-[#6e7b92]">
                  {{ primaryChartHint }}
                </p>
              </div>
            </div>
            <div
              v-if="primaryChartRows.length"
              class="h-[18.5rem]"
            >
              <canvas ref="primaryChartRef" />
            </div>
            <div
              v-else
              class="grid h-[18.5rem] place-items-center rounded-[1rem] border border-dashed border-[rgba(218,226,238,0.95)] bg-[#fbfcff] px-6 text-center"
            >
              <p class="max-w-sm text-[0.9rem] leading-7 text-[#6e7b92]">
                {{ primaryChartEmptyText }}
              </p>
            </div>
            <div
              v-if="isReportMode || isClientUser"
              class="mt-4 grid gap-2 sm:grid-cols-2"
            >
              <div
                v-for="item in primaryChartLegend"
                :key="item.label"
                class="rounded-[0.95rem] border border-[rgba(227,233,243,0.95)] bg-[#fbfcff] px-3 py-3"
              >
                <div class="flex items-center gap-2">
                  <span
                    class="h-2.5 w-2.5 rounded-full"
                    :style="{ backgroundColor: item.color }"
                  />
                  <p class="text-[0.82rem] font-extrabold text-[#182033]">
                    {{ item.label }}
                  </p>
                </div>
                <p class="mt-1 text-[0.76rem] leading-5 text-[#6e7b92]">
                  {{ item.description }}
                </p>
              </div>
            </div>

            <div
              v-else-if="primaryChartLegend.length"
              class="mt-4 flex flex-wrap gap-2"
            >
              <span
                v-for="item in primaryChartLegend"
                :key="item.label"
                class="inline-flex items-center gap-2 rounded-full border border-[rgba(227,233,243,0.95)] bg-[#fbfcff] px-3 py-1 text-[0.78rem] font-semibold text-[#4b5563]"
              >
                <span
                  class="h-2.5 w-2.5 rounded-full"
                  :style="{ backgroundColor: item.color }"
                />
                {{ item.label }}
              </span>
            </div>
          </section>

          <section
            class="rounded-[1.15rem] border border-[rgba(227,233,243,0.95)] bg-white p-4"
          >
            <div class="mb-3 flex items-center justify-between gap-3">
              <div>
                <p
                  class="text-[0.72rem] font-bold uppercase tracking-[0.14em] text-[#718096]"
                >
                  Service progress
                </p>
                <p class="mt-1 text-[0.88rem] text-[#6e7b92]">
                  {{ secondaryChartHint }}
                </p>
              </div>
            </div>
            <div
              v-if="serviceProgressRows.length"
              :style="{ height: serviceProgressChartHeight }"
            >
              <canvas ref="secondaryChartRef" />
            </div>
            <div
              v-else
              class="grid h-[18.5rem] place-items-center rounded-[1rem] border border-dashed border-[rgba(218,226,238,0.95)] bg-[#fbfcff] px-6 text-center"
            >
              <div class="max-w-md">
                <p class="text-[0.95rem] font-bold text-[#182033]">
                  No service progress data available yet.
                </p>
                <p class="mt-2 text-[0.9rem] leading-7 text-[#6e7b92]">
                  Service progress will appear once tasks are available.
                </p>
              </div>
            </div>
            <div
              v-if="serviceProgressRows.length"
              class="mt-4 grid gap-2 sm:grid-cols-3"
            >
              <div
                v-for="item in serviceProgressLegend"
                :key="item.label"
                class="rounded-[0.95rem] border border-[rgba(227,233,243,0.95)] bg-[#fbfcff] px-3 py-3"
              >
                <div class="flex items-center gap-2">
                  <span
                    class="h-2.5 w-2.5 rounded-full"
                    :style="{ backgroundColor: item.color }"
                  />
                  <p class="text-[0.82rem] font-extrabold text-[#182033]">
                    {{ item.label }}
                  </p>
                </div>
                <p class="mt-1 text-[0.76rem] leading-5 text-[#6e7b92]">
                  {{ item.description }}
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </CardContent>

    <Dialog v-model:open="scopeDialogOpen">
      <DialogContent
        class="max-h-[90vh] w-[min(92vw,44rem)] max-w-2xl overflow-hidden rounded-[1.4rem] border border-[rgba(215,226,239,0.95)] bg-white p-0 shadow-[0_28px_80px_rgba(15,23,42,0.22)]"
      >
        <div class="border-b border-[rgba(227,233,243,0.95)] px-6 py-5">
          <DialogHeader class="gap-y-2 text-left">
            <DialogTitle class="text-[1.3rem] font-extrabold text-[#182033]">
              Select client account
            </DialogTitle>
            <DialogDescription class="text-[#6e7b92]">
              Choose one accessible client account to scope this KPI overview.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div class="space-y-4 px-6 py-5">
          <div class="relative">
            <Search
              class="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#94a3b8]"
            />
            <Input
              v-model="scopeSearch"
              class="h-11 rounded-[0.95rem] border-[rgba(211,220,233,1)] bg-white pl-10 text-[0.95rem] text-[#243049] focus-visible:ring-[#8eb4ff] focus-visible:ring-offset-0"
              placeholder="Search client accounts"
              type="text"
            />
          </div>

          <div
            v-if="clientsLoading"
            class="space-y-2"
          >
            <Skeleton
              v-for="n in 4"
              :key="`client-skel-${n}`"
              class="h-[4.3rem] rounded-[1rem] bg-stone-100"
            />
          </div>

          <div
            v-else
            class="max-h-[18rem] space-y-2 overflow-y-auto pr-1"
          >
            <button
              v-for="client in filteredClients"
              :key="client.client_id"
              type="button"
              class="flex w-full items-start justify-between gap-3 rounded-[1rem] border px-4 py-3 text-left transition-colors"
              :class="
                String(selectedClientId) === String(client.client_id)
                  ? 'border-[#8eb4ff] bg-[#f4f8ff]'
                  : 'border-[rgba(216,228,240,0.95)] bg-white hover:bg-[#f8fbff]'
              "
              @click="selectedClientId = client.client_id"
            >
              <div class="min-w-0 space-y-1">
                <p
                  class="truncate text-[0.95rem] font-extrabold text-[#182033]"
                >
                  {{ client.company_name }}
                </p>
                <p class="text-[0.84rem] text-[#6e7b92]">
                  Client ID {{ client.client_id }}
                </p>
                <p
                  v-if="client.package_name"
                  class="text-[0.84rem] text-[#6e7b92]"
                >
                  Package:
                  <span class="font-semibold text-[#1b2436]">{{
                    client.package_name
                  }}</span>
                </p>
              </div>

              <Badge
                variant="outline"
                class="rounded-full border-[rgba(142,180,255,0.35)] bg-[#f6f9ff] px-2.5 py-[0.2rem] text-[0.68rem] font-extrabold uppercase tracking-[0.08em] text-[#2855a6]"
              >
                {{ client.active_services_count }} Services
              </Badge>
            </button>

            <div
              v-if="!filteredClients.length"
              class="rounded-[1rem] border border-dashed border-[rgba(216,228,240,0.95)] bg-[#fbfcff] px-4 py-5 text-center text-[0.92rem] text-[#6e7b92]"
            >
              {{
                scopeSearch.trim()
                  ? "No matching accounts found."
                  : "No available accounts found."
              }}
            </div>
          </div>
        </div>

        <DialogFooter
          class="border-t border-[rgba(227,233,243,0.95)] px-6 py-4"
        >
          <Button
            variant="outline"
            type="button"
            class="rounded-[0.95rem] border-[rgba(49,125,255,0.22)] bg-[#f6f9ff] px-4 py-2 font-bold text-[#2855a6] hover:bg-[#eef4ff]"
            :disabled="scopeLoading"
            @click="cancelScopeSelection"
          >
            Cancel
          </Button>
          <Button
            type="button"
            class="rounded-[0.95rem] bg-[#1f7aff] px-4 py-2 font-bold text-white hover:bg-[#1f7aff]"
            :disabled="!selectedClientId || scopeLoading"
            @click="confirmSpecificScope"
          >
            {{ scopeLoading ? "Loading..." : "Apply Scope" }}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </Card>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import Chart from "chart.js/auto";
import { Search } from "lucide-vue-next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import http from "@/services/http";
import { useAuthStore } from "@/store/auth";
import type {
  DashboardClientOption,
  DashboardFilters,
  DashboardKpiItem,
  DashboardRole,
} from "@/types/dashboard";

type NormalizedKpiRow = {
  id: string;
  packageName: string;
  serviceName: string;
  kpiName: string;
  progress: number;
  status: string;
  openTasks: number;
  inProgressTasks: number;
  completedTasks: number;
  totalTasks: number;
  latestActivityAt: string | null;
  slaText: string;
};

type AggregatedServiceRow = NormalizedKpiRow & {
  packageName: string;
};

const props = defineProps<{
  data: DashboardKpiItem[];
  loading?: boolean;
  clients?: DashboardClientOption[];
  clientsLoading?: boolean;
  filters?: DashboardFilters;
  role?: DashboardRole;
  error?: string | null;
  reportMode?: boolean;
}>();

const authStore = useAuthStore();
const primaryChartRef = ref<HTMLCanvasElement | null>(null);
const secondaryChartRef = ref<HTMLCanvasElement | null>(null);
let primaryChart: Chart | null = null;
let secondaryChart: Chart | null = null;

const selectedScope = ref<"all" | "specific">("all");
const selectedClientId = ref<string | number | null>(null);
const selectedClientName = ref<string | null>(null);
const scopeDialogOpen = ref(false);
const scopeSearch = ref("");
const scopeLoading = ref(false);
const scopeError = ref<string | null>(null);
const scopedRows = ref<DashboardKpiItem[]>([]);
const scopeBeforeDialog = ref<{
  selectedScope: "all" | "specific";
  selectedClientId: string | number | null;
  selectedClientName: string | null;
} | null>(null);

const roleKey = computed(() => String(props.role ?? "").toLowerCase());
const isClientUser = computed(() => roleKey.value === "client");
const isReportMode = computed(() => props.reportMode === true);
const showScopeControls = computed(() => !isClientUser.value);
const clientsLoading = computed(() => props.clientsLoading === true);

const sourceRows = computed(() =>
  selectedScope.value === "specific" ? scopedRows.value : props.data ?? []
);

const normalizedRows = computed<NormalizedKpiRow[]>(() => {
  return (sourceRows.value ?? []).map((item) => {
    const packageName = item.package_name?.trim() || "Unassigned";
    const serviceName = item.service_name?.trim() || "Unassigned Service";
    const totalTasks = safeNumber(item.total_tasks);
    const completedTasks = safeNumber(item.completed_tasks);
    const inProgressTasks = safeNumber(item.in_progress_tasks);
    const openTasks =
      item.open_tasks !== undefined && item.open_tasks !== null
        ? safeNumber(item.open_tasks)
        : Math.max(totalTasks - completedTasks - inProgressTasks, 0);
    const progress =
      totalTasks > 0
        ? clampPercent((completedTasks / totalTasks) * 100)
        : clampPercent(item.completion_rate ?? item.progress_percentage);

    return {
      id: `${item.service_id ?? item.service_name}-${
        item.package_id ?? item.package_name
      }-${item.kpi_name}`,
      packageName,
      serviceName,
      kpiName: item.kpi_name || "KPI",
      progress,
      status:
        item.health_status ??
        item.status ??
        serviceHealthStatus(
          totalTasks,
          completedTasks,
          inProgressTasks,
          openTasks
        ),
      openTasks,
      inProgressTasks,
      completedTasks,
      totalTasks,
      latestActivityAt: item.latest_activity_at ?? null,
      slaText: item.sla_status || item.sla_deadline || "No SLA target set",
    };
  });
});

const aggregatedRows = computed<AggregatedServiceRow[]>(() => {
  const groups = new Map<
    string,
    AggregatedServiceRow & { packages: Set<string>; kpis: Set<string> }
  >();

  for (const row of normalizedRows.value) {
    const key = `${row.serviceName}`.trim().toLowerCase();
    const existing = groups.get(key);
    const nextActivity = maxDate(
      existing?.latestActivityAt ?? null,
      row.latestActivityAt
    );

    if (!existing) {
      groups.set(key, {
        ...row,
        id: `service-${key || row.id}`,
        packageName: row.packageName,
        kpiName: row.kpiName,
        latestActivityAt: nextActivity,
        packages: new Set([row.packageName]),
        kpis: new Set([row.kpiName]),
      });
      continue;
    }

    existing.openTasks += row.openTasks;
    existing.inProgressTasks += row.inProgressTasks;
    existing.completedTasks += row.completedTasks;
    existing.totalTasks += row.totalTasks;
    existing.latestActivityAt = nextActivity;
    existing.progress =
      existing.totalTasks > 0
        ? clampPercent((existing.completedTasks / existing.totalTasks) * 100)
        : 0;
    existing.status = serviceHealthStatus(
      existing.totalTasks,
      existing.completedTasks,
      existing.inProgressTasks,
      existing.openTasks
    );
    existing.packages.add(row.packageName);
    existing.kpis.add(row.kpiName);
    existing.packageName =
      existing.packages.size === 1
        ? Array.from(existing.packages)[0]
        : "Multiple packages";
    existing.kpiName =
      existing.kpis.size === 1
        ? Array.from(existing.kpis)[0]
        : "Aggregated KPIs";
  }

  return Array.from(groups.values())
    .map((row) => ({
      id: row.id,
      packageName: row.packageName,
      serviceName: row.serviceName,
      kpiName: row.kpiName,
      progress: row.progress,
      status: row.status,
      openTasks: row.openTasks,
      inProgressTasks: row.inProgressTasks,
      completedTasks: row.completedTasks,
      totalTasks: row.totalTasks,
      latestActivityAt: row.latestActivityAt,
      slaText: row.slaText,
    }))
    .sort((a, b) => {
      const progressDiff = a.progress - b.progress;
      if (progressDiff !== 0) return progressDiff;
      const openDiff = b.openTasks - a.openTasks;
      if (openDiff !== 0) return openDiff;
      const totalDiff = b.totalTasks - a.totalTasks;
      if (totalDiff !== 0) return totalDiff;
      return a.serviceName.localeCompare(b.serviceName);
    });
});

const serviceProgressRows = computed(() => aggregatedRows.value);

const hasData = computed(() => normalizedRows.value.length > 0);
const currentError = computed(() => scopeError.value || props.error || null);
const loadingState = computed(
  () => props.loading === true || scopeLoading.value
);

const packageSummary = computed(() => {
  const counts = new Map<string, number>();

  for (const row of normalizedRows.value) {
    counts.set(row.packageName, (counts.get(row.packageName) ?? 0) + 1);
  }

  return Array.from(counts.entries())
    .map(([label, value]) => ({
      label,
      value,
      color: colorForText(label),
    }))
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
});

type HealthBucket = {
  label: "On Track" | "Progressing" | "Needs Review" | "No Activity";
  value: number;
  color: string;
  description: string;
};

const statusBuckets = computed<HealthBucket[]>(() => {
  const buckets: HealthBucket[] = [
    {
      label: "On Track",
      value: 0,
      color: "#10b981",
      description: "Completion is strong and task flow is steady.",
    },
    {
      label: "Progressing",
      value: 0,
      color: "#1f7aff",
      description: "Work is active and moving forward with some follow-up.",
    },
    {
      label: "Needs Review",
      value: 0,
      color: "#f59e0b",
      description: "This service has open work and should be checked soon.",
    },
    {
      label: "No Activity",
      value: 0,
      color: "#94a3b8",
      description: "No tasks have been recorded in the selected scope yet.",
    },
  ];

  for (const row of normalizedRows.value) {
    const bucket = bucketForStatus(row.status, row.progress);
    const target = buckets.find((item) => item.label === bucket);
    if (target) {
      target.value += 1;
    }
  }

  return buckets.filter((item) => item.value > 0);
});

const packageCount = computed(() => packageSummary.value.length);
const serviceCount = computed(() => aggregatedRows.value.length);
const openTaskCount = computed(() =>
  normalizedRows.value.reduce((sum, row) => sum + row.openTasks, 0)
);
const averageProgress = computed(() => {
  const totalTasks = normalizedRows.value.reduce(
    (sum, row) => sum + row.totalTasks,
    0
  );
  if (totalTasks <= 0) return 0;

  const completedTasks = normalizedRows.value.reduce(
    (sum, row) => sum + row.completedTasks,
    0
  );
  return clampPercent((completedTasks / totalTasks) * 100);
});
const serviceAttentionCount = computed(
  () =>
    aggregatedRows.value.filter(
      (row) => row.totalTasks > 0 && (row.progress < 50 || row.openTasks > 0)
    ).length
);

const summaryCards = computed(() => {
  if (isClientUser.value) {
    return [
      {
        label: "Package",
        value: packageSummary.value[0]?.label ?? "N/A",
        muted:
          packageSummary.value.length > 1
            ? `${packageSummary.value.length} packages detected in the current scope`
            : "Your availed package and included services",
        dot: packageSummary.value[0]?.color ?? "#1f7aff",
      },
      {
        label: "Included services",
        value: String(serviceCount.value),
        muted: "Services tracked within the current package",
        dot: "#10b981",
      },
      {
        label: "Open tasks",
        value: String(openTaskCount.value),
        muted: "Open items across the included services",
        dot: "#f97316",
      },
      {
        label: "Avg progress",
        value: `${averageProgress.value}%`,
        muted: `${serviceAttentionCount.value} services need attention`,
        dot: "#2855a6",
      },
    ];
  }

  return [
    {
      label: "Active packages",
      value: String(packageCount.value),
      muted: "Distinct packages visible in the current scope",
      dot: "#1f7aff",
    },
    {
      label: "Active services",
      value: String(serviceCount.value),
      muted: "Distinct services visible in the current scope",
      dot: "#10b981",
    },
    {
      label: "Open tasks",
      value: String(openTaskCount.value),
      muted: "Open items across the visible KPI rows",
      dot: "#f97316",
    },
    {
      label: "Avg progress",
      value: `${averageProgress.value}%`,
      muted: `${serviceAttentionCount.value} rows need attention`,
      dot: "#2855a6",
    },
  ];
});

const primaryChartLabel = computed(() =>
  isReportMode.value || isClientUser.value
    ? "Service health mix"
    : "Package distribution"
);

const primaryChartHint = computed(() =>
  isReportMode.value || isClientUser.value
    ? "Health buckets across the services in the selected scope, with quick explanations below."
    : "Package mix across the visible KPI rows."
);

const primaryChartEmptyText = computed(() =>
  isReportMode.value || isClientUser.value
    ? "Health mix will appear once the selected scope has service activity."
    : "Package distribution will appear once the selected scope has KPI rows."
);

const secondaryChartHint = computed(() =>
  selectedScope.value === "specific"
    ? `${isClientUser.value ? "Closed, On-going" : "Completed, in-progress"}, and open tasks for ${
        selectedClientName.value || "the selected client"
      }`
    : isClientUser.value
    ? "Closed, On-going, and open tasks for your account."
    : "Completed, in-progress, and open tasks across the accessible client scope."
);

const primaryChartRows = computed(() => {
  if (isReportMode.value || isClientUser.value) {
    return statusBuckets.value;
  }

  return packageSummary.value;
});

const primaryChartLegend = computed(() => {
  if (isReportMode.value || isClientUser.value) {
    return statusBuckets.value.map((item) => ({
      label: `${item.label} (${item.value})`,
      color: item.color,
      description: item.description,
    }));
  }

  return packageSummary.value.slice(0, 5).map((item) => ({
    label: `${shortenLabel(item.label, 18)} (${item.value})`,
    color: item.color,
  }));
});

const serviceProgressRowsSorted = computed(() =>
  [...serviceProgressRows.value].sort(
    (a, b) =>
      a.progress - b.progress ||
      b.openTasks - a.openTasks ||
      b.totalTasks - a.totalTasks ||
      a.serviceName.localeCompare(b.serviceName)
  )
);

const serviceProgressChartHeight = computed(() => {
  const rowCount = serviceProgressRowsSorted.value.length;
  const heightRem = Math.max(18.5, rowCount * 2.45);
  return `${heightRem}rem`;
});

const serviceProgressLegend = computed(() => [
  {
    label: isClientUser.value ? "Closed" : "Completed",
    color: "#10b981",
    description: "Tasks already finished in the selected scope.",
  },
  {
    label: isClientUser.value ? "On-going" : "In Progress",
    color: "#1f7aff",
    description: "Tasks actively moving forward right now.",
  },
  {
    label: "Open",
    color: "#f59e0b",
    description: "Tasks still waiting to be started or resolved.",
  },
]);

const scopeSummaryLabel = computed(() => {
  if (isClientUser.value) {
    return authStore.companyName || "Your Client Account";
  }

  if (selectedScope.value === "specific") {
    return (
      selectedClientName.value ||
      String(selectedClientId.value || "Selected client")
    );
  }

  return "All Client Accounts";
});

const chartDescription = computed(() => {
  if (isClientUser.value) {
    return "KPI performance for your account and included services.";
  }

  if (selectedScope.value === "specific") {
    return `Package and service KPI performance for ${scopeSummaryLabel.value}.`;
  }

  return "Package and service KPI performance across your accessible client accounts.";
});

const emptyTitle = computed(() => {
  if (isClientUser.value) {
    return "No KPI data is available for your current package yet.";
  }

  if (selectedScope.value === "specific") {
    return "No KPI data is available for the selected client yet.";
  }

  return "No KPI data available for the selected filters.";
});

const emptyDescription = computed(() => {
  if (isClientUser.value) {
    return "Once the included services start tracking activity, the compact charts and summary table will populate here.";
  }

  if (selectedScope.value === "specific") {
    return "Service and package analytics will appear once the selected client has tracked activity.";
  }

  return "KPI values will appear once the visible services and packages have tracked progress.";
});

const filteredClients = computed(() => {
  const term = scopeSearch.value.trim().toLowerCase();
  const clients = props.clients ?? [];

  return clients
    .filter((client) => {
      if (!term) return true;

      return [client.company_name, client.client_id, client.package_name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    })
    .slice()
    .sort((a, b) => a.company_name.localeCompare(b.company_name));
});

const showResetButton = computed(
  () => showScopeControls.value && selectedScope.value === "specific"
);

function safeNumber(value: number | undefined | null) {
  const num = Number(value ?? 0);
  return Number.isFinite(num) ? num : 0;
}

function clampPercent(value: number | undefined | null) {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) return 0;
  return Math.max(0, Math.min(Math.round(num), 100));
}

function formatPercent(value: number | undefined | null) {
  return `${clampPercent(value)}%`;
}

function bucketForStatus(status: string, progress: number) {
  const key = String(status ?? "").toLowerCase();

  if (key.includes("risk")) return "Needs Review";
  if (key.includes("warning")) return "Needs Review";
  if (
    key.includes("attention") ||
    key.includes("progress") ||
    key.includes("monitoring")
  )
    return "Progressing";
  if (key.includes("healthy") || key.includes("complete")) return "On Track";

  if (progress >= 80) return "On Track";
  if (progress >= 50) return "Progressing";
  if (progress > 0) return "Needs Review";
  return "No Activity";
}

function serviceHealthStatus(
  totalTasks: number,
  completedTasks: number,
  inProgressTasks: number,
  openTasks: number
) {
  if (totalTasks <= 0) {
    return "No Activity";
  }

  const completionRate =
    totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

  if (completedTasks >= totalTasks && totalTasks > 0) {
    return "On Track";
  }

  if (completionRate < 40 || (openTasks > 0 && inProgressTasks > 0)) {
    return "Needs Review";
  }

  if (openTasks > 0 || inProgressTasks > 0 || completionRate < 80) {
    return "Progressing";
  }

  return "On Track";
}

function colorForText(value: string) {
  const palette = [
    "#1f7aff",
    "#10b981",
    "#f97316",
    "#8b5cf6",
    "#14b8a6",
    "#ef4444",
  ];
  let hash = 0;

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }

  return palette[Math.abs(hash) % palette.length];
}

function shortenLabel(value: string, max = 24) {
  if (value.length <= max) return value;
  return `${value.slice(0, Math.max(1, max - 3)).trimEnd()}...`;
}

function maxDate(current: string | null, next: string | null) {
  if (!current) return next;
  if (!next) return current;

  const currentTime = new Date(current).getTime();
  const nextTime = new Date(next).getTime();
  if (Number.isNaN(currentTime)) return next;
  if (Number.isNaN(nextTime)) return current;

  return nextTime > currentTime ? next : current;
}

function destroyCharts() {
  primaryChart?.destroy();
  primaryChart = null;
  secondaryChart?.destroy();
  secondaryChart = null;
}

function renderPrimaryChart() {
  if (!primaryChartRef.value || !primaryChartRows.value.length) return;

  if (isReportMode.value || isClientUser.value) {
    const labels = statusBuckets.value.map((item) => item.label);
    const values = statusBuckets.value.map((item) => item.value);
    const colors = statusBuckets.value.map((item) => item.color);

    if (!labels.length) return;

    primaryChart = new Chart(primaryChartRef.value, {
      type: "doughnut",
      data: {
        labels,
        datasets: [
          {
            data: values,
            backgroundColor: colors,
            borderColor: "#ffffff",
            borderWidth: 3,
            hoverOffset: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "68%",
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label(context) {
                const total =
                  values.reduce((sum, value) => sum + value, 0) || 1;
                const current = Number(context.raw ?? 0);
                const percent = Math.round((current / total) * 100);
                return ` ${context.label}: ${current} (${percent}%)`;
              },
            },
          },
        },
      },
    });

    return;
  }

  const labels = packageSummary.value.map((item) =>
    shortenLabel(item.label, 20)
  );
  const values = packageSummary.value.map((item) => item.value);
  const colors = packageSummary.value.map((item) => item.color);

  if (!labels.length) return;

  primaryChart = new Chart(primaryChartRef.value, {
    type: "doughnut",
    data: {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: colors,
          borderColor: "#ffffff",
          borderWidth: 3,
          hoverOffset: 4,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      cutout: "68%",
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label(context) {
              const total = values.reduce((sum, value) => sum + value, 0) || 1;
              const current = Number(context.raw ?? 0);
              const percent = Math.round((current / total) * 100);
              return ` ${context.label}: ${current} (${percent}%)`;
            },
          },
        },
      },
    },
  });
}

function renderSecondaryChart() {
  if (!secondaryChartRef.value || !serviceProgressRowsSorted.value.length)
    return;

  const rows = serviceProgressRowsSorted.value;
  const labels = rows.map((item) => shortenLabel(item.serviceName, 30));
  const completedValues = rows.map((item) => item.completedTasks);
  const inProgressValues = rows.map((item) => item.inProgressTasks);
  const openValues = rows.map((item) => item.openTasks);
  const totals = rows.map((item) => item.totalTasks);
  const maxTotal = Math.max(...totals, 0);
  const completedLabel = isClientUser.value ? "Closed" : "Completed";
  const inProgressLabel = isClientUser.value ? "On-going" : "In Progress";

  secondaryChart = new Chart(secondaryChartRef.value, {
    type: "bar",
    data: {
      labels,
      datasets: [
        {
          label: completedLabel,
          data: completedValues,
          backgroundColor: "#10b981",
          borderRadius: 10,
          barPercentage: 0.74,
          categoryPercentage: 0.82,
          stack: "tasks",
        },
        {
          label: inProgressLabel,
          data: inProgressValues,
          backgroundColor: "#1f7aff",
          borderRadius: 10,
          barPercentage: 0.74,
          categoryPercentage: 0.82,
          stack: "tasks",
        },
        {
          label: "Open",
          data: openValues,
          backgroundColor: "#f59e0b",
          borderRadius: 10,
          barPercentage: 0.74,
          categoryPercentage: 0.82,
          stack: "tasks",
        },
      ],
    },
    options: {
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label(context) {
              const row = rows[context.dataIndex];
              const value = Number(context.raw ?? 0);
              const total = row?.totalTasks ?? maxTotal;
              const percent = total > 0 ? Math.round((value / total) * 100) : 0;
              return ` ${context.dataset.label}: ${value} (${percent}%)`;
            },
            afterBody(context) {
              const row = rows[context[0]?.dataIndex ?? 0];
              if (!row) return [];
              return [
                `Total tasks: ${row.totalTasks}`,
                `Progress: ${formatPercent(row.progress)}`,
              ];
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          beginAtZero: true,
          suggestedMax: Math.max(maxTotal, 1),
          grid: {
            color: "rgba(226,232,240,0.95)",
            borderDash: [4, 4],
          },
          ticks: {
            color: "rgba(71,85,105,0.9)",
            callback(value) {
              return `${value}`;
            },
          },
        },
        y: {
          stacked: true,
          grid: { display: false },
          ticks: {
            color: "rgba(71,85,105,0.95)",
            font: { size: 11, weight: "600" },
          },
        },
      },
    },
    plugins: [
      {
        id: "taskCountLabels",
        afterDatasetsDraw(currentChart) {
          const { ctx } = currentChart;

          ctx.save();
          ctx.fillStyle = "#0f172a";
          ctx.font = "600 11px Inter, ui-sans-serif, system-ui, sans-serif";
          ctx.textBaseline = "middle";

          currentChart.getDatasetMeta(2).data.forEach((bar, index) => {
            const row = rows[index];
            if (!row) return;

            const x = bar.x + 10;
            const y = bar.y;
            const text =
              row.totalTasks > 0
                ? `${row.completedTasks} / ${row.totalTasks}`
                : "No activity";
            ctx.textAlign = "left";
            ctx.fillText(text, x, y);
          });

          ctx.restore();
        },
      },
    ],
  });
}

function renderCharts() {
  destroyCharts();

  if (!hasData.value) return;

  renderPrimaryChart();
  renderSecondaryChart();
}

function buildAnalyticsParams() {
  const params: Record<string, string | number> = {};

  if (
    props.filters?.service_id !== null &&
    props.filters?.service_id !== undefined
  ) {
    params.service_id = props.filters.service_id;
  }
  if (
    props.filters?.package_id !== null &&
    props.filters?.package_id !== undefined
  ) {
    params.package_id = props.filters.package_id;
  }
  if (props.filters?.date_from) {
    params.date_from = props.filters.date_from;
  }
  if (props.filters?.date_to) {
    params.date_to = props.filters.date_to;
  }

  return params;
}

async function fetchSpecificScopeRows() {
  const clientId = String(selectedClientId.value || "").trim();
  if (!clientId) {
    scopedRows.value = [];
    return;
  }

  scopeLoading.value = true;
  scopeError.value = null;
  scopedRows.value = [];

  try {
    const params = {
      ...buildAnalyticsParams(),
      client_id: clientId,
    };
    const { data } = await http.get<{
      service_package_kpis?: DashboardKpiItem[];
    }>("/api/dashboard/analytics", { params });

    scopedRows.value = Array.isArray(data?.service_package_kpis)
      ? data.service_package_kpis
      : [];
    selectedClientName.value =
      props.clients?.find(
        (client) => String(client.client_id) === String(clientId)
      )?.company_name ?? selectedClientName.value;
  } catch (error) {
    scopedRows.value = [];
    scopeError.value =
      error instanceof Error
        ? error.message
        : "We could not load the selected client KPI data.";
  } finally {
    scopeLoading.value = false;
  }
}

function openScopeDialog() {
  scopeBeforeDialog.value = {
    selectedScope: selectedScope.value,
    selectedClientId: selectedClientId.value,
    selectedClientName: selectedClientName.value,
  };
  scopeSearch.value = "";
  scopeDialogOpen.value = true;
}

function resetScopeState() {
  selectedScope.value = "all";
  selectedClientId.value = null;
  selectedClientName.value = null;
  scopeError.value = null;
  scopedRows.value = [];
}

function resetToAllScope() {
  resetScopeState();
}

function cancelScopeSelection() {
  scopeDialogOpen.value = false;
  const previous = scopeBeforeDialog.value;
  if (!previous) return;

  selectedScope.value = previous.selectedScope;
  selectedClientId.value = previous.selectedClientId;
  selectedClientName.value = previous.selectedClientName;
  scopeBeforeDialog.value = null;
}

async function confirmSpecificScope() {
  const clientId = String(selectedClientId.value || "").trim();
  if (!clientId) return;

  selectedScope.value = "specific";
  scopeDialogOpen.value = false;
  scopeBeforeDialog.value = null;
  await fetchSpecificScopeRows();
}

function handleScopeSelect(value: string | number) {
  const next = String(value);

  if (next === "all") {
    resetToAllScope();
    return;
  }

  if (next === "specific") {
    openScopeDialog();
  }
}

function syncSelectedClientName() {
  if (!selectedClientId.value) {
    selectedClientName.value = null;
    return;
  }

  selectedClientName.value =
    props.clients?.find(
      (client) => String(client.client_id) === String(selectedClientId.value)
    )?.company_name ?? null;
}

watch(scopeDialogOpen, (open) => {
  if (open || !scopeBeforeDialog.value) return;

  const previous = scopeBeforeDialog.value;
  selectedScope.value = previous.selectedScope;
  selectedClientId.value = previous.selectedClientId;
  selectedClientName.value = previous.selectedClientName;
  scopeBeforeDialog.value = null;
});

watch(
  () => selectedClientId.value,
  () => {
    syncSelectedClientName();
  }
);

watch(
  () => props.clients,
  () => {
    syncSelectedClientName();
  },
  { deep: true }
);

watch(
  () => [
    props.filters?.service_id,
    props.filters?.package_id,
    props.filters?.date_from,
    props.filters?.date_to,
  ],
  async () => {
    if (selectedScope.value === "specific" && selectedClientId.value) {
      await fetchSpecificScopeRows();
      nextTick(renderCharts);
    }
  }
);

watch(
  () => [
    normalizedRows.value,
    serviceProgressRows.value,
    props.loading,
    scopeLoading.value,
    props.role,
    selectedScope.value,
  ],
  () => nextTick(renderCharts),
  { deep: true, immediate: true }
);

onMounted(() => {
  if (isClientUser.value) {
    selectedClientId.value = authStore.clientProfile?.client_id ?? null;
    selectedClientName.value = authStore.companyName || null;
  }
});

onBeforeUnmount(() => {
  destroyCharts();
});
</script>
